import URLSearchParams from '@ungap/url-search-params';
import {Logger} from '@vanti/vue-logger';

const logger = Logger.get('api');

/**
 */
class TimeoutError extends Error {
  /**
   * @param {string} message
   */
  constructor(message) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Defines an api endpoint and functions for talking to it.
 */
class Endpoint {
  /**
   * @param {string} path
   */
  constructor(path) {
    this.path = path;
  }

  /**
   * Utility to create the base api urls.
   * @return {string}
   */
  _baseUrl() {
    return this.path || '';
  }

  /**
   * @param {string} url
   * @param {URLSearchParams|Object} [searchParams]
   * @return {Promise<Response>}
   */
  get(url, searchParams) {
    const request = new Request(this._baseUrl() + url + Endpoint._toParamString(searchParams), {
      method: 'GET'
    });
    return Endpoint._fetchAndRetry(request);
  }

  /**
   * @param {string} url
   * @param {URLSearchParams|Object} [searchParams]
   * @return {Promise<Object>}
   */
  getJson(url, searchParams) {
    return this.get(url, searchParams)
        .then(res => res.json());
  }

  /**
   * @param {string} url
   * @param {*} body
   * @return {Promise<Response>}
   */
  post(url, body) {
    const request = new Request(this._baseUrl() + url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
    return Endpoint._fetchAndRetry(request);
  }

  /**
   * Convert some object type into a search parameter string with a leading ? or the empty string.
   *
   * @param {URLSearchParams|object} [searchParams]
   * @return {string}
   * @private
   */
  static _toParamString(searchParams) {
    let paramString = '';
    if (typeof searchParams === 'object') {
      const urlParams = new URLSearchParams();
      for (const key of Object.getOwnPropertyNames(searchParams)) {
        urlParams.append(key, searchParams[key]);
      }
      searchParams = urlParams;
    }
    if (searchParams instanceof URLSearchParams) {
      paramString = searchParams.toString();
    }
    if (paramString.length > 0 && !paramString.startsWith('?')) {
      paramString = `?${paramString}`;
    }
    return paramString;
  }


  /**
   * @param {Request} request
   * @return {Promise}
   */
  static _fetchWithTimeout(request) {
    const timeout = Endpoint._sleep(5000)
        .then(() => {
          throw new TimeoutError(`request timed out, url (${request.url})`);
        });
    return Promise.race([
      fetch(request),
      timeout
    ]);
  }

  /**
   * @param {Request} request
   * @return {Promise<Response>}
   */
  static _fetchAndRetry(request) {
    const retry = request.clone();
    return Endpoint._fetchWithTimeout(request)
        .catch(error => {
          logger.warn(error.message, ` - retrying (${request.url})`);
          return Endpoint._retryTimeout()
              .then(() => Endpoint._fetchWithTimeout(retry));
        })
        .then(res => {
          if (!res.ok) {
            throw new Error(`Bad response from request: ${res.status} (${res.statusText}) ${request.url}`);
          }
          return res;
        });
  }

  /**
   * @param {number} ms
   * @return {Promise}
   */
  static _sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * @return {Promise}
   */
  static _retryTimeout() {
    return Endpoint._sleep(1000);
  }
}

export const SideLights = 'side_led';
export const FrontLights = 'front_led';

export default {
  Endpoint,
  sleep: Endpoint._sleep,
  led: new Endpoint('http://localhost:8080/v1/led'),
  info: new Endpoint('http://localhost:8080/v1/info'),
  settings: new Endpoint('http://localhost:8080/v1/settings'),
  volume: new Endpoint('http://localhost:8080/v1/audio/volume'),
  iadea: {
    auth: new Endpoint('http://localhost:8080/v2/oauth2/token'),
    lights: new Endpoint('http://localhost:8080/v2/hardware/light'),
    switch: new Endpoint('http://localhost:8080/v2/app/switch')
  }
};
